/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import { ccEnum } from '../../CCEnum';
import ClassUtil from '../../ClassUtil';
import CLog, { LogTypes } from '../../ComponentLogger';
import TypeStrings from '../../TypeStrings';
import ComponentReference from './ComponentReference';
import IInjectable, { IInjectableOptions } from './IInjectable';
import { ReferenceGroup } from './ReferenceGroup';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Serialized class containing all properties needed
 * for specific [[ReferencedComponent]] instance injection.
 */
@cc._decorator.ccclass("InjectableComponent")
export default class InjectableComponent<T> implements IInjectable<T>
{
    /** [[ReferenceGroup]] for this instance. */
    @property({ type: ccEnum.ReferenceGroup, visible: true })
    protected referenceGroup: ReferenceGroup = ReferenceGroup.MainGame;
    /** Public getter for [[ReferenceGroup]]. */
    public get ReferenceGroup(): ReferenceGroup { return this.referenceGroup; }

    /** Instance ID representing specific instance of same [[ReferencedComponent]]. */
    @property({ visible: true })
    protected instanceID: string = "";
    /** Public getter for particular instance's id. */
    public get InstanceID(): string { return this.instanceID; }
    public set InstanceID(instanceID: string)  { this.instanceID = instanceID; }

    /** Injected component. */
    protected value: T = null;
    /** Public getter for injected component which will attempt self-injection. */
    public get Value(): T { return this.GetValue(); }
    /** Public setter for injected component. */
    public set Value(value: T) { this.SetValue(value); }
    /** Executes function if value present, passing value as arg. */
    public WithValue<R>(
        fnValue: (args: T) => R,
        fnElse: ((...args: any[]) => R) | R | void = null): R
    {
        if (!this.HasValue())
        {
            if (fnElse === null || typeof(fnElse) !== TypeStrings.function) { return (fnElse as any) as R; }

            const retVal: R | void = (fnElse as Function)();
            return (retVal === undefined ? null : retVal) as R;
        }

        return fnValue(this.value);
    }

    /** Type of [[ReferencedComponent]] instance. */
    protected contextType: { new(): any } | string = null;

    private _injectOnce: boolean = false;
    private _hasInjected: boolean = false;

    /** @hidden */
    constructor(
        referenceGroup: ReferenceGroup = null,
        instanceID: string = null,
        c: { new(): T } = null)
    {
        this.SetContext(c, { ReferenceGroup: referenceGroup, InstanceID: instanceID });
    }

    /** Attempts to inject self and return value. */
    public GetValue(): T
    {
        return this.injectSelf();
    }

    public SetValue(value: T): this
    {
        this.value = value;
        this.hasValue = value !== null && value !== undefined;
        return this;
    }

    /** Returns false if null or undefined. */
    public HasValue(): boolean
    {
        this.injectSelf();
        return this.hasValue;
    }

    protected hasValue: boolean = false;

    /**
     * Set the context for the injection to pull correct instance.
     * @param contextType Type of [[ReferencedComponent]] to fetch.
     * @param referenceGroup Specific instance [[ReferenceGroupd]].
     * @param instanceID Specific instance ID.
     */
    public SetContext(
        contextType: { new(): T } | string | { name: string } = null,
        options: IInjectableOptions = {}): this
    {
        if (contextType)
        {
            if (typeof(contextType) === TypeStrings.string)
            {
                this.contextType = contextType as string;
            }
            else
            {
                let contextString: string = ClassUtil.getName(contextType);

                if (contextString) { this.contextType = contextString; }
            }
        }
        if (options.ReferenceGroup) { this.referenceGroup = options.ReferenceGroup; }
        if (options.InstanceID) { this.instanceID = options.InstanceID; }
        if (options.InjectOnce === true) { this._injectOnce = true; }

        return this;
    }

    /** Will return value if set, if not will attempt to fetch from [[ComponentReference]]. */
    protected injectSelf(): T
    {
        if (this.hasValue || (this._injectOnce && this._hasInjected)) { return this.value; }

        if (!this.contextType)
        {
            CLog.error('No context set.', null, LogTypes.Initialization);
            return null;
        }

        const result: T = ComponentReference.Get(this.contextType, this.referenceGroup, this.instanceID);

        this.SetValue(result);
        this._hasInjected = true;

        return result;
    }
}
